// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
// @greenlight-spec: 28-it-dashboard-ui.md
/**
 * `{token}` holes in catalog URL templates. Non-global: a global regex would
 * carry `lastIndex` across `.test()` calls and mis-report the next field.
 */
export const CATALOG_PLACEHOLDER_RE = /\{[a-z0-9_]+\}/i;
const CATALOG_PLACEHOLDER_CAPTURE_RE = /\{([a-z0-9_]+)\}/gi;
/** True when a URL template still carries an unreplaced `{placeholder}`. */
export function hasUnresolvedPlaceholder(value) {
    return CATALOG_PLACEHOLDER_RE.test(value);
}
/** Unique `{token}` names in template order of first appearance. */
export function extractPlaceholderTokens(templates) {
    const seen = new Set();
    const ordered = [];
    for (const template of templates) {
        const re = new RegExp(CATALOG_PLACEHOLDER_CAPTURE_RE.source, 'gi');
        for (const match of template.matchAll(re)) {
            const token = match[1];
            if (token === undefined || seen.has(token))
                continue;
            seen.add(token);
            ordered.push(token);
        }
    }
    return ordered;
}
/** Title-case a token (`account_id` → `Account Id`) for undeclared placeholders. */
export function titleCasePlaceholderToken(token) {
    return token
        .split('_')
        .filter((part) => part.length > 0)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}
export function normalizePlaceholderValue(raw, normalize) {
    const trimmed = raw.trim();
    if (normalize === 'lowercase_hyphenate') {
        return trimmed.toLowerCase().replaceAll('_', '-');
    }
    if (normalize === 'salesforce_my_domain') {
        const host = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').split(/[/?#]/)[0] ?? '';
        return host.replace(SALESFORCE_ORG_HOST_SUFFIX_RE, '').toLowerCase();
    }
    return trimmed;
}
/** The org-specific hosts a pasted Salesforce URL can carry the My Domain name in. */
const SALESFORCE_ORG_HOST_SUFFIX_RE = /\.(?:my\.salesforce\.com|my\.salesforce-setup\.com|my\.site\.com|lightning\.force\.com)$/i;
const SALESFORCE_OWNED_HOST_RE = /(?:^|\.)(?:salesforce|force|salesforce-setup|site|salesforce-sites|visualforce|cloudforce)\.com$/i;
const DNS_LABELS_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*$/i;
/** True for a My Domain name (`acme`, `acme--uat.sandbox`) rather than a URL or a shared Salesforce host. */
export function isSalesforceMyDomain(value) {
    return DNS_LABELS_RE.test(value) && !SALESFORCE_OWNED_HOST_RE.test(value);
}
export function resolveCatalogUrlPlaceholder(declared, token) {
    const found = declared?.find((placeholder) => placeholder.token === token);
    if (!found)
        return { token, label: titleCasePlaceholderToken(token) };
    return {
        token: found.token,
        label: found.label ?? titleCasePlaceholderToken(token),
        ...(found.hint ? { hint: found.hint } : {}),
        ...(found.example ? { example: found.example } : {}),
        ...(found.normalize ? { normalize: found.normalize } : {}),
        ...(found.format ? { format: found.format } : {}),
    };
}
/** Declared metadata merged with tokens actually present in the given templates. */
export function placeholdersForTemplates(templates, declared) {
    return extractPlaceholderTokens(templates).map((token) => resolveCatalogUrlPlaceholder(declared, token));
}
/**
 * The placeholder values stored URLs were resolved from — the inverse of
 * {@link applyUrlPlaceholders}. Null when any URL no longer fits its template,
 * or when two URLs disagree on one token's value.
 */
export function matchUrlPlaceholders(pairs) {
    const values = {};
    for (const { template, url } of pairs) {
        const tokens = [];
        const pattern = template
            .split(new RegExp(CATALOG_PLACEHOLDER_CAPTURE_RE.source, 'gi'))
            .map((part, index) => {
            if (index % 2 === 0)
                return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            tokens.push(part);
            return '([^/?#]+)';
        })
            .join('');
        const match = new RegExp(`^${pattern}$`, 'i').exec(url);
        if (!match)
            return null;
        for (const [i, token] of tokens.entries()) {
            const value = match[i + 1] ?? '';
            if (values[token] !== undefined && values[token] !== value)
                return null;
            values[token] = value;
        }
    }
    return values;
}
/**
 * Substitute `{token}` holes. Empty/missing values stay as the original
 * `{token}` so unresolved-placeholder validation can still catch them.
 */
export function applyUrlPlaceholders(template, values, declared) {
    return template.replace(CATALOG_PLACEHOLDER_CAPTURE_RE, (match, token) => {
        const raw = values[token];
        if (raw === undefined || raw.trim() === '')
            return match;
        const meta = resolveCatalogUrlPlaceholder(declared, token);
        return normalizePlaceholderValue(raw, meta.normalize);
    });
}
//# sourceMappingURL=placeholders.js.map